<template>
  <div>
    <NavBar />
    <!-- One line under the nav bar (SPEC §4.5): whose move it is (or the result), the two players,
         and the game's actions; a message takes the place of the first two while it shows. A
         rematch offer sits before ⋯; where the line is too short, the two move up to a line of
         their own above it. -->
    <div class="gameline" :class="{ offer: rematchShown }">
      <span v-if="messageText" class="msg" :class="messageClass" role="status">{{ messageText }}</span>
      <h3 v-else-if="notFound" class="msg">{{ t('game-not-found', { id: $route.params.id }) }}</h3>
      <template v-else-if="game && gameData">
        <span class="pill" :class="pillClass">{{ pillText }}</span>
        <span class="who">
          <template v-for="(seat, i) in presenceSeats">
            <span v-if="i === 1" :key="'vs' + i" class="vs">vs</span>
            <!-- A new message from this player during your move pulses above their name (SPEC §5.6):
                 a zero-width anchor at the start of the name holds the bubble. -->
            <span v-if="seat && pulsing.includes(seat.id)" :key="'p' + i" class="pulse-anchor"><button type="button" class="pulse on-seat"
                  :aria-label="t('chat-bubble', { name: seat.displayName })" @click="openChat"><svg viewBox="0 0 40 34"><path :d="BUBBLE"/><text x="20" y="18" class="pulse-text">{{ t('chat-bubble-text') }}</text></svg></button></span>
            <button :key="i" type="button" class="seat" :class="{ long: isLongName(seat) }" :disabled="mode !== 'session' || !seat"
                    :title="seat && mode === 'session' ? presenceLabel(seat) : null"
                    @click="presenceTip = presenceTip === seat.id ? null : seat.id"><PlayerName :colour="i === 0 ? 'black' : 'white'" :player="seat" :you="isYou(seat)" /><span v-if="seat && mode === 'session' && presence[seat.id]" class="here"></span></button>
          </template>
          <span v-if="handicapLabel" class="handicap">{{ handicapLabel }}</span>
          <button v-if="pulsing.includes('spectators')" type="button" class="pulse after-names" :aria-label="t('chat-bubble-spectators')" @click="openChat"><svg viewBox="0 0 40 34"><path :d="BUBBLE"/><text x="20" y="18" class="pulse-text">{{ t('chat-bubble-text') }}</text></svg></button>
        </span>
      </template>
      <span v-else class="space"></span>
      <!-- A new game between the same players, colours swapped (SPEC §6.1); for players only. -->
      <button v-if="rematchShown" type="button" class="rematch" :disabled="rematching" @click="startRematch">{{ t('replay-rematch') }}</button>
      <GameActions v-if="actions.length" :actions="actions" :guard="guard" :heading="t('game-number', { id: $route.params.id })" @action="onAction"/>
    </div>
    <div v-if="presenceTip" class="presence-tip">{{ presenceLabel({id:presenceTip}) }}</div>
    <MessageBox v-model="deleteBox"/>

    <a ref="downloadSgf" :href="sgf.url" :download="sgf.filename" />


    <template v-if="share">
      <component v-for="(section, i) in shareSections" :key="i" :is="section" :share="share"/>
    </template>

    <div class="view">
      <div v-if="mode === 'session'">
        <GameSession ref="view" :game="game" :player="player" :chat="chat" @message="onMessage" @completed="loadGame" @presence="onPresence"
                     @chat="onLiveChat" @joined="fetchChat" @dialog="onDialog" @chat-toggle="toggleChat" />
      </div>
      <div v-else-if="mode === 'replay'">
        <GameReplay ref="view" :game="game" :player="player" :chat="chat" @message="onMessage" @reload="loadGame" @chat-toggle="toggleChat">
          <template v-if="chat && !phone" #chat>
            <div ref="chatSlot" class="chat-slot" :style="{ height: chat.shown ? `${replayPanelHeight}px` : '0' }">
              <GameChat :chat="chat" layout="panel" :frame="{ left: 0, top: 0, width: replayPanelWidth, height: replayPanelHeight }" :playerIds="playerIds" canJump @jump="showMove" />
            </div>
          </template>
        </GameReplay>
      </div>
      <div v-else-if="!notFound">
        {{ statusText }}
      </div>
    </div>
    <!-- The chat: a window from the bottom on a phone, a panel beside the board on a desktop. -->
    <GameChat v-if="chat && (phone || mode === 'session')" :chat="chat" :layout="phone ? 'sheet' : 'panel'" :frame="chatFrame"
              :playerIds="playerIds" :canJump="mode === 'replay'" @jump="showMove" />
    <DebugTool v-if="debugTool && game" :game="game"/>

  </div>
</template>

<script>
import NavBar from './NavBar';
import GameActions from './GameActions';
import PlayerName from './PlayerName';
import DebugTool from './DebugTool';
import GameSession from './GameSession';
import GameReplay from './GameReplay';
import GameChat from './GameChat';
import Chat from '@/chat';
import Game from '@/game/Game';
import { Stone } from '@/game/Board';
import api, { RestError } from '@/api/api';
import text, { handicapText } from '@/text';

// The pill names a player in at most six characters, so that the players and the handicap keep
// their room on the line (the full name is right beside it).
function shortName(name) {
  const chars = [...name];
  return chars.length > 6 ? `${chars.slice(0, 6).join('')}…` : name;
}
import { t, errorText } from '@/texts';
import MessageBox from './MessageBox';
import { seats } from '@/players';
import extensions from '@/extensions';
import rematchOffer from '@/rematch';

export default {
  name: 'GamePage',

  data() {
    return {
      notFound: false,
      rematching: false,
      // The game this page shows, kept because $route changes before the page is destroyed.
      gameId: Number(this.$route.params.id),
      presence: {},
      presenceTip: null,
      mode: null,
      sgf: { url: '#', filename: 'slowgo.sgf' },
      sgfName: 'slowgo.sgf',
      player: null,
      gameData: null,
      createdBy: 0,
      deleteBox: null,
      game: null,
      messageText: null,
      messageClass: 'info',
      statusText: t('page-loading'),
      shareSections: extensions.shareSections,
      // Opened from the create page (?send=1): a module may offer sending it at once.
      arrived: !!this.$route.query.send,
      // The game's chat (chat.js, SPEC §5.6) and where its window or panel sits.
      chat: null,
      chatFrame: {},
      replayPanelWidth: 340,
      replayPanelHeight: 400,
      dialogShown: false,
      BUBBLE: 'M6 2h28a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H16l-8 7v-7H6a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z',
    }
  },

  computed: {
    phone() { return this.$store.state.isMobile; },

    playerIds() { return (this.gameData && this.gameData.players || []).map(p => p.id); },

    // During your move a new message doesn't open the chat on a phone: a bubble pulses instead.
    holdsChat() {
      return this.phone && this.mode === 'session' && !!this.player && !!this.game && this.isInGame && this.isMyTurn;
    },

    // Who has new messages waiting behind a pulsing bubble: player ids, or 'spectators'.
    pulsing() {
      if(!this.chat || !this.phone || this.chat.shown) return [];
      const s = seats(this.gameData);
      const ids = [s.black && s.black.id, s.white && s.white.id];
      const who = new Set(this.chat.unread.map(m => (ids.includes(m.user.id) ? m.user.id : 'spectators')));
      return [...who];
    },

    presenceSeats() { const s = seats(this.gameData); return [s.black, s.white]; },
    // What a module needs to offer sending this game (extensions.shareSections): the creator is
    // offered it while a game with an opponent has no moves.
    share() {
      if(!this.gameData || !this.game) return null;
      const me = this.$store.state.user;
      const id = Number(this.$route.params.id);
      const players = this.gameData.players || [];
      // Not the AI: there is nobody to send it to.
      const opponent = me && this.createdBy === me.id && players.length === 2 && !this.gameData.setup.aiLevel
        ? players.find(p => p.id !== me.id) : null;
      return {
        kind: 'game',
        id,
        grid: this.gameData.setup.grid,
        handicap: this.gameData.setup.handicap || 0,
        path: `/games/${id}`,
        viewer: me ? me.displayName : '',
        opponent: opponent ? opponent.displayName : null,
        offer: !!opponent && this.mode === 'session' && !this.game.hasMoves,
        arrived: this.arrived,
      };
    },

    // A rematch is offered to a player of a finished game while it has just finished for them
    // (SPEC §6.1): from the moment the game ends until they leave the page. Once they have left,
    // opening the game again replays it without the offer (rematch.js remembers that).
    canRematch() {
      const me = this.$store.state.user;
      if(!this.player || this.mode !== 'replay' || !me) return false;
      return !rematchOffer.over(me.id, this.gameId);
    },

    rematchShown() {
      return this.canRematch && !this.messageText && !this.notFound;
    },

    // The creator may delete the game while nothing has been played (no record after the .set).
    canDelete() {
      const me = this.$store.state.user;
      return !!me && this.createdBy === me.id && this.mode === 'session' && !!this.game && !this.game.hasMoves;
    },

    // The game's actions for this viewer (SPEC §5.3): a player's in play, the SGF in replay.
    actions() {
      if(this.mode === 'replay')
        return [{ key: 'downloadSgf', text: t('nav-download-sgf') }];
      if(this.mode !== 'session' || !this.player)
        return [];
      const off = !!this.guard;
      const list = [{ key: 'count', text: t('nav-count'), disabled: off }];
      // The AI doesn't take back its moves (plan 2026-10-28-ai-games.md, question 3).
      if(!this.gameData.setup.aiLevel)
        list.push({ key: 'takeBackForOpponent', text: t('nav-take-back-for-opponent'), disabled: off });
      if(this.canDelete)
        list.push({ key: 'deleteGame', text: t('nav-delete-game') });
      list.push({ key: 'resign', text: t('nav-resign'), disabled: off, danger: true });
      return list;
    },

    // Why a player's actions are unavailable now, if they are.
    guard() {
      if(this.mode !== 'session' || !this.player) return null;
      if(!this.isInGame) return t('guard-not-in-game');
      if(!this.isMyTurn) return t('guard-not-your-turn');
      return null;
    },

    debugTool() {
      return process.env.VUE_APP_DEBUG_TOOL === 'Y';
    },

    isInGame() {
      return this.game && this.game.status === 'in-progress';
    },

    isMyTurn() {
      return this.player === this.game.nextPlayer || this.player === Stone.BOTH;
    },

    // The handicap, after the players (SPEC §4.5): everyone following the game needs to know it.
    handicapLabel() {
      return this.gameData ? handicapText(this.gameData.setup.handicap || 0) : '';
    },

    // The pill at the start of the line: whose move it is, in words, or the result.
    pillText() {
      if(!this.game) return '';
      if(this.game.status === 'in-progress' && this.player) {
        if(this.isMyTurn) return t('game-your-move');
        const next = seats(this.gameData)[this.game.nextPlayer === Stone.WHITE ? 'white' : 'black'];
        return t('game-waiting-for', { name: next ? shortName(next.displayName) : t('seat-empty') });
      }
      return text.status(this.game);
    },

    pillClass() {
      if(!this.game) return null;
      if(this.game.status === 'in-progress')
        return this.player && this.isMyTurn ? 'mine' : 'wait';
      if(this.game.status === 'completed' && this.game.winner && this.player && this.player !== Stone.BOTH)
        return this.game.winner === this.player ? 'won' : 'lost';
      return 'done';
    },
  },

  components: {
    NavBar,
    PlayerName,
    GameActions,
    DebugTool,
    GameSession,
    GameReplay,
    GameChat,
    MessageBox
  },

  methods: {
    // ---- the chat (SPEC §5.6)
    async startChat() {
      const me = this.$store.state.user;
      if(this.chat || !me || !this.game) return;
      const chat = new Chat(this.gameId, me.id);
      this.chat = chat;
      await chat.load(!this.player);
      // Messages already new when the page opens count as just arrived, without a sound.
      this.onChatArrived(chat.unread, false);
    },

    // New messages: a sound for others' messages, then the chat opens unless it waits (a pulsing
    // bubble during your move on a phone, or a dialog being shown).
    onChatArrived(list, sound = true) {
      const others = list.filter(m => m.user.id !== this.chat.me);
      if(!others.length) return;
      if(sound) this.$audio.play('message');
      if(this.chat.shown) { this.chat.markRead(); return; }
      if(this.holdsChat || (this.phone && this.dialogShown)) return;
      this.openChat();
    },

    onLiveChat(m) {
      if(this.chat && !this.chat.loading) this.onChatArrived(this.chat.merge([m]));
    },

    async fetchChat() {
      if(this.chat) this.onChatArrived(await this.chat.fetchNew());
    },

    openChat() {
      if(!this.chat) return;
      this.placeChat();
      this.chat.open();
    },

    toggleChat() {
      if(this.chat.shown) this.chat.close(); else this.openChat();
    },

    onDialog(shown) {
      this.dialogShown = shown;
      if(!shown && this.chat && this.chat.unread.length && !this.holdsChat && !this.chat.shown) this.openChat();
    },

    showMove(n) {
      const v = this.$refs.view;
      if(v && v.showMove) v.showMove(n);
    },

    // Where the window or panel sits: the phone window's top a fifth of the way down the board;
    // the desktop panel 16 px right of the board, as tall as it (the board moves left, then the
    // panel narrows, if needed).
    placeChat() {
      const canvas = this.$el && this.$el.querySelector('.view canvas');
      if(!canvas) return;
      const view = this.$el.querySelector('.view');
      if(this.phone) {
        const r = canvas.getBoundingClientRect();
        this.chatFrame = { top: Math.max(0, Math.round(r.top + r.height * 0.22)) };
      } else if(this.mode === 'session') {
        view.style.transform = '';
        let r = canvas.getBoundingClientRect();
        const need = r.right + 16 + 300 + 16 - window.innerWidth;
        if(need > 0) {
          view.style.transform = `translateX(-${Math.min(need, Math.max(0, r.left - 8))}px)`;
          r = canvas.getBoundingClientRect();
        }
        // Still too narrow (the board already at the left): a narrower panel, down to 220 px.
        const width = Math.max(220, Math.min(300, window.innerWidth - r.right - 32));
        this.chatFrame = { left: r.right + 16 + window.scrollX, top: r.top + window.scrollY, width, height: r.height };
      } else {
        const slot = this.$refs.chatSlot;
        const r = canvas.getBoundingClientRect();
        if(slot) {
          const s = slot.getBoundingClientRect();
          this.replayPanelWidth = Math.round(s.width) || 340;
          this.replayPanelHeight = Math.max(280, Math.round(r.bottom - s.top));
        }
      }
    },

    onResize() { if(this.chat && this.chat.shown) this.placeChat(); },

    // A finished game has no live connection: its chat is asked for every 15 s while the page is
    // in front, and at once when it comes back (SPEC §5.6).
    pollChat() {
      if(this.mode === 'replay' && !document.hidden) this.fetchChat();
    },

    onVisibility() {
      if(document.hidden || !this.chat) return;
      this.chat.markRead();
      if(this.mode === 'replay') this.fetchChat();
    },

    onPresence(msg) { this.$set(this.presence, msg.user.id, msg.here); },
    presenceLabel(p) { return t(this.presence[p.id] ? 'presence-here' : 'presence-away'); },
    // The viewer's own seat is shown as #seat-you, unless they play both colours.
    // A name that may need cutting: longer than its first character and the "…".
    isLongName(seat) {
      return !!seat && !this.isYou(seat) && [...(seat.displayName || '')].length > 2;
    },

    isYou(seat) {
      const me = this.$store.state.user;
      return !!seat && !!me && seat.id === me.id && this.player !== Stone.BOTH;
    },

    // The viewer's colour(s) from the game's player setting and the signed-in account; none
    // (spectator) while the setting is incomplete.
    roleFromSeats(data) {
      const me = this.$store.state.user;
      const s = seats(data);
      const black = s.black && s.black.id, white = s.white && s.white.id;
      if(!me || !black || !white) return null;
      if(black === me.id && white === me.id) return Stone.BOTH;
      if(black === me.id) return Stone.BLACK;
      if(white === me.id) return Stone.WHITE;
      return null;
    },

    t,

    onAction(key) {
      if(key === 'downloadSgf') this.downloadSgf();
      else this.onMenuAction(key);
    },

    downloadSgf() {
      if(!this.game) return;

      const data = encodeURIComponent(this.game.getSgf());

      this.sgf = {
        url: `data:text/plain;charset=utf-8,${data}`,
        filename: `slowgo-game-${this.game.id}.sgf`
      };

      this.$nextTick(() => {
        const a = this.$refs['downloadSgf'];
        a.click();
      });
    },

    beginMove() {
      if(!this.isInGame) {
        this.showMessage('未在游戏中，不能操作', 'warn');
        return false;
      }

      if(!this.isMyTurn) {
        this.showMessage('不该你走，不能操作', 'warn');
        return false;
      }

      return true;
    },

    confirmDelete() {
      this.deleteBox = {
        text: t('delete-game-confirm'),
        okText: '确认',
        ok: () => this.deleteGame(),
        cancel: () => {},
      };
    },

    // A new game between the same players, with the colours swapped and the same board (SPEC §6.1);
    // a game played alone is played alone again. It opens like a game just created.
    startRematch() {
      this.rematching = true;
      this.rematch(() => { this.rematching = false; });
    },

    async rematch(done) {
      const s = seats(this.gameData);
      const players = (this.gameData.players || []).map(p => ({ id: p.id }));
      // Colours swap, except in a handicap game, which is played again as it was (SPEC §6.1).
      const handicap = this.gameData.setup.handicap || 0;
      const setup = { type: 'standard', grid: this.gameData.setup.grid, blackPlayer: handicap ? s.black.id : s.white.id };
      if(handicap) setup.handicap = handicap;
      // Against the AI: the same level.
      const aiLevel = this.gameData.setup.aiLevel;
      if(aiLevel) setup.aiLevel = aiLevel;
      try {
        const { id } = await api.call('POST', api.GAMES, { setup, players });
        this.$router.push({ name: 'game', params: { id }, query: players.length > 1 && !aiLevel ? { send: '1' } : {} });
      } catch(e) {
        const code = e.msg || String(e);
        this.showMessage(code === 'forbidden' && aiLevel ? t('error-ai-not-allowed') : errorText(code), 'warn');
      } finally {
        done();
      }
    },

    async deleteGame() {
      try {
        await api.call('DELETE', `${api.GAMES}/${this.$route.params.id}`);
        this.$router.push({ name: 'list' });
      } catch(e) {
        this.showMessage(errorText(e.msg || String(e)), 'warn');
      }
    },

    onMenuAction(action) {
      if(action === 'deleteGame') {
        this.confirmDelete();
        return;
      }

      const v = this.$refs['view'];
      if(!v || !(action in v)) {
        this.showMessage('不支持', 'warn');
        return;
      }

      if(!this.beginMove())
        return;

      v[action]();
    },

    async showMessage(msg, cls='info') {
      this.messageText = this.messageText ? this.messageText + msg : msg;
      this.messageClass = cls;
      await this.sleep(3000);
      this.messageText = null;
    },

    sleep(ms) {
      return new Promise((resolve) => { setTimeout(resolve, ms); });
    },

    onMessage(msg, style='info') {
      this.showMessage(msg, style);
    },

    async loadGame() {
      try {
        const data = await api.get(api.GAMES, this.$route.params.id);
        this.gameData = { setup: data.setup, players: data.players };
        this.createdBy = data.createdBy || 0;
        this.player = this.roleFromSeats(data);
        this.game = new Game();
        this.game.load(data);

        switch(this.game.status) {
          case 'in-progress':
            this.mode = 'session';
            break;
          case 'completed':
            this.mode = 'replay';
            break;
        }
      } catch(err) {
        if(err instanceof RestError && err.msg === 'not-found') {
          this.notFound = true;
        } else if(err instanceof RestError) {
          this.statusText = t('load-failed', { reason: err.uiMsg });
        } else {
          this.statusText = t('load-failed', { reason: String(err) });
        }
      }
    },
  },

  watch: {
    // Placing your stone (or the move passing to the opponent) opens the chat when something new
    // came in during your move (SPEC §5.6).
    holdsChat(now, before) {
      if(before && !now && this.chat && this.chat.unread.length && !this.chat.shown && !(this.phone && this.dialogShown)) this.openChat();
    },
    mode(now) {
      if(now) this.$nextTick(() => { this.startChat(); this.onResize(); });
    },
    'chat.shown'(shown) {
      if(!shown && !this.phone && this.mode === 'session') {
        const view = this.$el.querySelector('.view');
        if(view) view.style.transform = '';
      }
      if(shown) this.$nextTick(() => this.placeChat());
    },
  },

  async created() {
    if (this.arrived) this.$router.replace({ path: this.$route.path });
    await this.loadGame();
  },

  mounted() {
    this.chatPoll = setInterval(() => this.pollChat(), 15000);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
  },

  // Leaving the page ends this player's rematch offer for the game (SPEC §6.1), whether they
  // started a rematch, went back to the list or opened another page.
  beforeDestroy() {
    clearInterval(this.chatPoll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    const me = this.$store.state.user;
    // The game's own id, not `$route`, which already points at the page being opened.
    if (this.player && this.mode === 'replay' && me)
      rematchOffer.endOffer(me.id, this.gameId);
  },
}
</script>

<style scoped>
.gameline {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 8px;
  box-sizing: border-box;
  font-size: 15px;
}

/* With the rematch offer the names keep their width, and when the line is too short the offer and
   ⋯ wrap to a line above them (wrap-reverse). */
.gameline.offer {
  flex-wrap: wrap-reverse;
  height: auto;
  min-height: 44px;
  padding-block: 4px;
  row-gap: 2px;
}

.gameline.offer .who {
  flex: 1 0 auto;
  max-width: calc(100% - 96px);
}

button.rematch {
  flex: none;
  margin-left: auto;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #8b5e34;
  border-radius: 8px;
  background: #f6ead6;
  color: #8b5e34;
  font-family: inherit;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
}

button.rematch:disabled {
  opacity: .45;
  cursor: default;
}

.pill {
  flex: none;
  padding: 2px 9px;
  border-radius: 12px;
  font-size: 13px;
  white-space: nowrap;
}

.pill.mine { background: #0175ff; color: #fff; }
.pill.wait, .pill.done { background: #eee; color: #555; }
.pill.won { background: #e3f5e3; color: #0a0; }
.pill.lost { background: #fde8e8; color: #d00; }

.who {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
}

.vs {
  color: #999;
  margin: 0 6px;
  font-size: 13px;
}

/* A player's name; tapping it says whether they have the game open. */
.seat {
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  padding: 4px 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
}

/* A long name gives way with "…" (the black one first), but keeps its stone and first character;
   a short one (你) never shrinks. */
.seat.long {
  min-width: 3.2em;
}

.seat:not(.long) {
  flex-shrink: 0;
}

.who .seat.long:first-of-type {
  flex-shrink: 100;
}

.handicap {
  flex: none;
  margin-left: 8px;
  color: #888;
}

.seat:disabled {
  color: inherit;
  cursor: default;
}

/* Has the game open. */
.here {
  flex: none;
  width: 6px;
  height: 6px;
  margin-left: 3px;
  border-radius: 50%;
  background: #258348;
}

.msg {
  flex: 1;
  margin: 0;
  text-align: left;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.space {
  flex: 1;
}

/* A new message during your move, on a phone (SPEC §5.6): a speech bubble that grows and shrinks
   in turn, showing #chat-bubble-text in yellow while it is big. */
.pulse {
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  z-index: 5;
}

.pulse svg {
  width: 20px;
  height: 17px;
  overflow: visible;
  fill: #0175ff;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, .3));
  /* Grows from its tail, so it stays pointing at the name. */
  transform-origin: 20% 100%;
  animation: chat-grow .7s ease-in-out infinite alternate;
}

.pulse-text {
  fill: #ffd400;
  font-size: 15px;
  font-weight: bold;
  text-anchor: middle;
  dominant-baseline: middle;
  animation: chat-text .7s ease-in-out infinite alternate;
}

@keyframes chat-grow {
  from { transform: scale(1); }
  to { transform: scale(1.7); }
}

@keyframes chat-text {
  0%, 40% { opacity: 0; }
  100% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .pulse svg { animation: none; transform: scale(1.4); }
  .pulse-text { animation: none; opacity: 1; }
}

/* Above the sender's name, over its first characters, out of the line's flow. */
.pulse-anchor {
  position: relative;
  flex: none;
  width: 0;
  align-self: stretch;
}

.pulse.on-seat {
  position: absolute;
  left: 12px;
  top: -17px;
}

.pulse.after-names { margin-left: 6px; }

.chat-slot {
  position: relative;
  margin-top: 12px;
}

.presence-tip {
  margin-top: -6px;
  font-size: 12px;
  color: #777;
}

.info {
  color: #00f;
}

.warn {
  color: #f00;
}

.succ {
  color: #0a0;
}
</style>
